import {
  BuyerLegalType,
  ContractNature,
  FoaPart,
  Regulation,
  ServiceCategory,
  type EventContextDto,
} from "../../autogen/bff-api";

export interface FoaPartBanner {
  messageKey: string;
  status: "info" | "warning";
  /** Interpolation values for the message — the legal basis the classification rests on. */
  values?: Record<string, string>;
}

// Mirrors the LOA floor and FOA's national tier in the BFF's ProcurementThresholds.kt — keep in sync.
export const OUTSIDE_LOA_THRESHOLD_NOK = 500_000;
export const FOA_NATIONAL_THRESHOLD_NOK = 1_300_000;
// Mirrors eeaSpecialServicesNok in the BFF's ProcurementThresholds.kt — keep in sync.
export const FOA_SPECIAL_SERVICES_EEA_THRESHOLD_NOK = 8_700_000;
export const UTILITIES_SPECIAL_SERVICES_EEA_THRESHOLD_NOK = 11_600_000;
export const UTILITIES_SUPPLIES_SERVICES_EEA_THRESHOLD_NOK = 5_000_000;
export const UTILITIES_WORKS_EEA_THRESHOLD_NOK = 62_900_000;

// Mirrors RegimeClassifier.derive in the BFF — keep in sync.
export const simplifiedFlowThresholdNok = (
  regulation: Regulation | null | undefined,
  contractNature?: ContractNature | null,
  serviceCategory?: ServiceCategory | null,
): number => {
  if (regulation === Regulation.Foa) return FOA_NATIONAL_THRESHOLD_NOK;
  if (regulation === Regulation.Forsyningsforskriften) {
    if (contractNature === ContractNature.Services && isSpecialServiceCategory(serviceCategory)) {
      return UTILITIES_SPECIAL_SERVICES_EEA_THRESHOLD_NOK;
    }
    return contractNature === ContractNature.Works
      ? UTILITIES_WORKS_EEA_THRESHOLD_NOK
      : UTILITIES_SUPPLIES_SERVICES_EEA_THRESHOLD_NOK;
  }
  return OUTSIDE_LOA_THRESHOLD_NOK;
};

const isSpecialServiceCategory = (serviceCategory: ServiceCategory | null | undefined): boolean =>
  serviceCategory === ServiceCategory.SpecialServices || serviceCategory === ServiceCategory.HealthSocialEducation;

// Mirrors SourcingEventService.governingRegulation in the BFF — keep in sync.
export const governingRegulationOf = (
  context: EventContextDto,
  organizationDefault: Regulation | null | undefined,
): Regulation | null => classifyingContextOf(context)?.regulation ?? organizationDefault ?? null;

export const classificationServiceCategoryOf = (context: EventContextDto): ServiceCategory | null =>
  effectiveServiceCategoryOf(classifyingContextOf(context));

const classifyingContextOf = (context: EventContextDto) =>
  context.type === "Public" || context.type === "Private" ? context : null;

// Mirrors EventContext.Public.effectiveServiceCategory in the BFF — keep in sync.
export const effectiveServiceCategoryOf = (
  context: { serviceCategory?: ServiceCategory | null; specialServices?: boolean | null } | null | undefined,
): ServiceCategory | null =>
  context?.serviceCategory ?? (context?.specialServices === true ? ServiceCategory.SpecialServices : null);

export const valueIsPartI = (
  regulation: Regulation | null | undefined,
  estimatedValueNok: number | null | undefined,
  contractNature?: ContractNature | null,
  serviceCategory?: ServiceCategory | null,
): boolean =>
  (regulation === Regulation.Foa || regulation === Regulation.Forsyningsforskriften) &&
  estimatedValueNok != null &&
  estimatedValueNok >= OUTSIDE_LOA_THRESHOLD_NOK &&
  estimatedValueNok < simplifiedFlowThresholdNok(regulation, contractNature, serviceCategory);

export const valueIsFoaPartI = (
  regulation: Regulation | null | undefined,
  estimatedValueNok: number | null | undefined,
): boolean => regulation === Regulation.Foa && valueIsPartI(regulation, estimatedValueNok);

export const valueIsUtilitiesPartIWhateverIsBought = (
  regulation: Regulation | null | undefined,
  estimatedValueNok: number | null | undefined,
): boolean => regulation === Regulation.Forsyningsforskriften && valueIsPartI(regulation, estimatedValueNok);

export const impliesSimplifiedFlow = (
  regulation: Regulation | null | undefined,
  estimatedValueNok: number | null | undefined,
  contractNature?: ContractNature | null,
  serviceCategory?: ServiceCategory | null,
): boolean =>
  estimatedValueNok != null &&
  estimatedValueNok < simplifiedFlowThresholdNok(regulation, contractNature, serviceCategory);

// Mirrors FoaPart.runsInSimplifiedFlow in the BFF — keep in sync.
export const partRunsInSimplifiedFlow = (foaPart: FoaPart | null | undefined): boolean =>
  foaPart === FoaPart.OutsideFoa || foaPart === FoaPart.PartI;

export const isPartIHealthSocialEducationService = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  contractNature: ContractNature | null | undefined,
  serviceCategory: ServiceCategory | null | undefined,
): boolean =>
  regulation === Regulation.Foa &&
  foaPart === FoaPart.PartI &&
  contractNature === ContractNature.Services &&
  serviceCategory === ServiceCategory.HealthSocialEducation;

// Mirrors EventContext.Public.isAnnouncedPartI in the BFF — keep in sync.
export const isAnnouncedPartI = (context: {
  regulation?: Regulation | null;
  foaPart?: FoaPart | null;
  announcedOnDoffin?: boolean | null;
} | null | undefined): boolean =>
  (context?.regulation === Regulation.Foa || context?.regulation === Regulation.Forsyningsforskriften) &&
  context.foaPart === FoaPart.PartI &&
  context.announcedOnDoffin === true;

export const formatThresholdNok = (thresholdNok: number): string => thresholdNok.toLocaleString("nb-NO");

// Banner copy is consumed via a dynamic t(banner.messageKey) call, so langen can't see it.
// These magic comments register the keys statically — keep them verbatim with the switches below.
// t('This is a Del I procurement, announced voluntarily on Doffin')
// t('This procurement is below the Procurement Act threshold — it should run in the simplified flow')
// t('This is a Del I procurement — it should run in the simplified flow')
// t('FOA Part I — health, social and education services, which you may announce voluntarily on Doffin')
// t('FOA Part II — national procurement, announced on Doffin')
// t('FOA Part II — special services above their EEA threshold ({{threshold}} NOK), announced on Doffin and TED')
// t('FOA Part II — announced as an EU notice in Doffin and forwarded to TED')
// t('FOA Part III — over the EEA threshold, announced on Doffin and TED')
// t('FOA Part IV — special services')
// t('Choose the governing regulation to classify the procurement')
// t('Choose a contract type to classify the procurement')
// t('Set the buyer legal type below to classify the procurement')
// t('The procurement is classified when you continue')
// t('Forsyningsforskriften Part I — under the EEA threshold in {{provision}}. Only the basic principles apply, and there is no announcement duty, so it should run in the simplified flow')
// t('Forsyningsforskriften Part II — over the EEA threshold in {{provision}}. Announcement on Doffin and TED is required')
// t('Forsyningsforskriften Part I — special or health and social services under {{threshold}} NOK (§ 5-2 (2)). Only the basic principles apply, and there is no announcement duty, so it should run in the simplified flow')
// t('Forsyningsforskriften Part III — special or health and social services from {{threshold}} NOK (§ 5-2 (2)), announced on Doffin and TED')
export interface ClassificationInputs {
  regulation: Regulation | null | undefined;
  foaPart: FoaPart | null | undefined;
  estimatedValueNok?: number | null;
  contractNature?: ContractNature | null;
  buyerLegalType?: BuyerLegalType | null;
  specialServicesAboveEea?: boolean;
  noticeGoesToTed?: boolean;
  serviceCategory?: ServiceCategory | null;
  announcedOnDoffin?: boolean | null;
}

export const foaPartBanner = ({
  regulation,
  foaPart,
  estimatedValueNok,
  contractNature,
  buyerLegalType,
  specialServicesAboveEea = false,
  noticeGoesToTed = false,
  serviceCategory,
  announcedOnDoffin,
}: ClassificationInputs): FoaPartBanner | null => {
  if (regulation != null && foaPart != null) {
    if (isAnnouncedPartI({ regulation, foaPart, announcedOnDoffin })) {
      return { messageKey: "This is a Del I procurement, announced voluntarily on Doffin", status: "info" };
    }
    if (isPartIHealthSocialEducationService(regulation, foaPart, contractNature, serviceCategory)) {
      return {
        messageKey: "FOA Part I — health, social and education services, which you may announce voluntarily on Doffin",
        status: "info",
      };
    }
    if (specialServicesAboveEea && foaPart === FoaPart.PartIi) {
      return {
        messageKey:
          "FOA Part II — special services above their EEA threshold ({{threshold}} NOK), announced on Doffin and TED",
        status: "info",
        values: { threshold: formatThresholdNok(FOA_SPECIAL_SERVICES_EEA_THRESHOLD_NOK) },
      };
    }
    if (noticeGoesToTed && foaPart === FoaPart.PartIi && regulation === Regulation.Foa) {
      return { messageKey: "FOA Part II — announced as an EU notice in Doffin and forwarded to TED", status: "info" };
    }
    const utilitiesSpecial = utilitiesSpecialServicesBanner(regulation, foaPart, contractNature, serviceCategory);
    if (utilitiesSpecial) return utilitiesSpecial;
    const provision = thresholdProvision(regulation, contractNature);
    return {
      messageKey: messageKeyFor(regulation, foaPart),
      status: "info",
      ...(provision ? { values: { provision } } : {}),
    };
  }
  // Unclassified: name the input that is still missing, but only once the buyer has given it
  // something to classify, and in the order the buyer meets them.
  if (estimatedValueNok == null) return null;
  if (regulation == null) {
    return { messageKey: "Choose the governing regulation to classify the procurement", status: "info" };
  }
  if (contractNature == null) {
    return { messageKey: "Choose a contract type to classify the procurement", status: "info" };
  }
  if (buyerLegalType == null && needsBuyerLegalType(regulation, contractNature)) {
    return { messageKey: "Set the buyer legal type below to classify the procurement", status: "warning" };
  }
  // Every input is in. The part itself is derived server-side, which for a draft that has not been
  // classified yet happens on continue — so promise that rather than naming an input as missing.
  return { messageKey: "The procurement is classified when you continue", status: "info" };
};

const utilitiesSpecialServicesBanner = (
  regulation: Regulation,
  foaPart: FoaPart,
  contractNature: ContractNature | null | undefined,
  serviceCategory: ServiceCategory | null | undefined,
): FoaPartBanner | null => {
  const special =
    serviceCategory === ServiceCategory.SpecialServices || serviceCategory === ServiceCategory.HealthSocialEducation;
  if (regulation !== Regulation.Forsyningsforskriften || contractNature !== ContractNature.Services || !special) {
    return null;
  }
  const values = { threshold: formatThresholdNok(UTILITIES_SPECIAL_SERVICES_EEA_THRESHOLD_NOK) };
  if (foaPart === FoaPart.PartI) {
    return {
      messageKey:
        "Forsyningsforskriften Part I — special or health and social services under {{threshold}} NOK (§ 5-2 (2)). Only the basic principles apply, and there is no announcement duty, so it should run in the simplified flow",
      status: "info",
      values,
    };
  }
  if (foaPart === FoaPart.PartIii) {
    return {
      messageKey:
        "Forsyningsforskriften Part III — special or health and social services from {{threshold}} NOK (§ 5-2 (2)), announced on Doffin and TED",
      status: "info",
      values,
    };
  }
  return null;
};

const messageKeyFor = (regulation: Regulation, foaPart: FoaPart): string => {
  switch (foaPart) {
    case FoaPart.OutsideFoa:
      return "This procurement is below the Procurement Act threshold — it should run in the simplified flow";
    case FoaPart.PartI:
      return regulation === Regulation.Foa
        ? "This is a Del I procurement — it should run in the simplified flow"
        : "Forsyningsforskriften Part I — under the EEA threshold in {{provision}}. Only the basic principles apply, and there is no announcement duty, so it should run in the simplified flow";
    case FoaPart.PartIi:
      return regulation === Regulation.Foa
        ? "FOA Part II — national procurement, announced on Doffin"
        : "Forsyningsforskriften Part II — over the EEA threshold in {{provision}}. Announcement on Doffin and TED is required";
    case FoaPart.PartIii:
      return "FOA Part III — over the EEA threshold, announced on Doffin and TED";
    case FoaPart.PartIv:
      return "FOA Part IV — special services";
    default: {
      const _exhaustive: never = foaPart;
      return _exhaustive;
    }
  }
};

// Mirrors ThresholdSet.eeaThresholdFor in the BFF's ProcurementThresholds.kt.
const thresholdProvision = (
  regulation: Regulation,
  contractNature: ContractNature | null | undefined,
): string | null => {
  if (regulation !== Regulation.Forsyningsforskriften) return null;
  return contractNature === ContractNature.Works ? "§ 5-2 (1) b" : "§ 5-2 (1) a";
};

// Mirrors ThresholdSet.eeaThresholdFor in the BFF's ProcurementThresholds.kt.
export const needsBuyerLegalType = (
  regulation: Regulation,
  contractNature: ContractNature | null | undefined,
): boolean => regulation === Regulation.Foa && contractNature !== ContractNature.Works;

// Mirrors the BFF's RegulationSupportsPublishing gate (FieldKeys.kt) — keep in sync.
export const regulationSupportsPublishing = (regulation: Regulation | null | undefined): boolean =>
  regulation !== Regulation.Konsesjonskontraktforskriften;
